import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from '../../firebase/firebaseConfig';
import { useAuth } from '../../context/AuthContext';
import { useMarketplace } from '../../context/MarketplaceContext';
import { useChat } from '../../context/ChatContext';
import { useNotification } from '../../context/NotificationContext';
import { chatModel } from '../../data/chatModel';
import { notificationModel } from '../../data/notificationModel';
import { storage } from '../../firebase/firebaseConfig';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import ChatWindow from '../Chat/ChatWindow';
import PaymentProofViewer from './PaymentProofViewer';
import TrackingNumberInput from './TrackingNumberInput';
import ProductReviewForm from '../Review/ProductReviewForm';
import StoreReviewForm from '../Review/StoreReviewForm';
import ShippingLabelPrintModal from './ShippingLabelPrintModal';
import PackingSlipPrintModal from './PackingSlipPrintModal';
import { compressImage } from '../../utils/imageUtils';
import { getTrackingUrl } from '../../utils/trackingNumberUtils';
import { useReview } from '../../context/ReviewContext';

const OrderDetail = () => {
  const { t } = useTranslation();
  const { orderId } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { updateOrderStatus, getStoreSubscription } = useMarketplace();
  const { findOrCreateChat } = useChat();
  const { createNotification } = useNotification();
  const { loadProductReviews, loadStoreReviews } = useReview();
  const [order, setOrder] = useState(null);
  const [store, setStore] = useState(null);
  const [packageType, setPackageType] = useState('free');
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [trackingNumber, setTrackingNumber] = useState('');
  const [courierCompany, setCourierCompany] = useState(null);
  const [customCourierCompany, setCustomCourierCompany] = useState(null);
  const [chatId, setChatId] = useState(null);
  const [showChat, setShowChat] = useState(false);
  const [otherUser, setOtherUser] = useState(null);
  const [showProductReviewForm, setShowProductReviewForm] = useState(null); // productId or null
  const [showStoreReviewForm, setShowStoreReviewForm] = useState(false);
  const [showShippingLabelModal, setShowShippingLabelModal] = useState(false);
  const [showPackingSlipModal, setShowPackingSlipModal] = useState(false);
  const [productReviews, setProductReviews] = useState([]);
  const [storeReviews, setStoreReviews] = useState([]);
  const [hasReviewedProducts, setHasReviewedProducts] = useState(false);
  const [hasReviewedStore, setHasReviewedStore] = useState(false);

  useEffect(() => {
    fetchOrder();
  }, [orderId]);

  const fetchOrder = async () => {
    try {
      const orderRef = doc(db, 'orders', orderId);
      const orderSnap = await getDoc(orderRef);
      
      if (orderSnap.exists()) {
        const orderData = orderSnap.data();
        setOrder({ id: orderSnap.id, ...orderData });
        setTrackingNumber(orderData.trackingNumber || '');
        setCourierCompany(orderData.courierCompany || null);
        setCustomCourierCompany(orderData.customCourierCompany || null);
        
        // Fetch store data and package type
        if (orderData.storeId) {
          try {
            const storeRef = doc(db, 'stores', orderData.storeId);
            const storeSnap = await getDoc(storeRef);
            if (storeSnap.exists()) {
              setStore({ id: storeSnap.id, ...storeSnap.data() });
            }
            
            // Get package subscription
            try {
              const subscription = await getStoreSubscription(orderData.storeId);
              setPackageType(subscription?.packageType || 'free');
            } catch (error) {
              console.error('Error fetching subscription:', error);
              setPackageType('free');
            }
          } catch (error) {
            console.error('Error fetching store:', error);
          }
        }
        
        // Setup chat
        if (currentUser) {
          const otherUserId = currentUser.uid === orderData.buyerId ? orderData.sellerId : orderData.buyerId;
          const otherUserName = currentUser.uid === orderData.buyerId ? orderData.sellerName : orderData.buyerName;
          setOtherUser({ id: otherUserId, name: otherUserName });
          
          // Find or create chat
          try {
            const chat = await findOrCreateChat(
              chatModel.types.ORDER,
              otherUserId,
              orderId
            );
            setChatId(chat);
          } catch (error) {
            console.error('Error setting up chat:', error);
          }
        }
      }
    } catch (error) {
      console.error('Error fetching order:', error);
    } finally {
      setLoading(false);
    }
  };

  // Load reviews when order status changes to delivered or received
  useEffect(() => {
    if (order && (order.status === 'delivered' || order.status === 'received') && currentUser) {
      loadReviewsForOrder(order);
    }
  }, [order?.status, order?.id, currentUser?.uid]);

  const handleUploadPaymentProof = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file
    if (!file.type.startsWith('image/')) {
      alert('กรุณาเลือกไฟล์รูปภาพเท่านั้น');
      return;
    }
    
    if (file.size > 5 * 1024 * 1024) {
      alert('ไฟล์มีขนาดใหญ่เกินไป (ไม่เกิน 5MB)');
      return;
    }

    setUploading(true);
    try {
      // Upload to Firebase Storage
      const storageRef = ref(storage, `payment-proofs/${orderId}/${Date.now()}_${file.name}`);
      await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(storageRef);

      // Update order with payment proof
      await updateOrderStatus(orderId, 'pending_payment', {
        paymentProof: downloadURL,
        paymentProofUploadedAt: new Date(),
        paymentProofUploadedBy: currentUser.uid,
        paymentConfirmed: false // ยังไม่ยืนยันจากผู้ขาย
      });

      // ส่งการแจ้งเตือนให้ผู้ขาย
      try {
        await createNotification(
          order.sellerId,
          notificationModel.types.PAYMENT_PROOF_UPLOADED,
          'มีหลักฐานการโอนเงินใหม่',
          `${t('orders.sellerNotifyPayment')} #${order.orderNumber} ${t('orders.pleaseCheckAndConfirm')}`,
          { orderId: orderId, orderNumber: order.orderNumber }
        );
      } catch (notifError) {
        console.error('Error creating payment proof notification:', notifError);
      }

      alert(`${t('orders.paymentProofUploaded')}\n\n${t('orders.notifySeller')}`);
      fetchOrder();
    } catch (error) {
      console.error('Error uploading payment proof:', error);
      if (error.code === 'storage/unauthorized') {
        alert('❌ ไม่มีสิทธิ์อัพโหลดไฟล์ กรุณาติดต่อผู้ดูแลระบบ\n\nหมายเหตุ: ต้อง deploy storage.rules ก่อน');
      } else if (error.code === 'storage/quota-exceeded') {
        alert('❌ เกินโควตาการเก็บข้อมูล กรุณาติดต่อผู้ดูแลระบบ');
      } else {
        alert('❌ เกิดข้อผิดพลาดในการอัพโหลด: ' + error.message);
      }
    } finally {
      setUploading(false);
    }
  };

  const handleUpdateTrackingNumber = async (newTrackingNumber, newCourierCompany, newCustomCourierCompany) => {
    if (!newTrackingNumber || !newTrackingNumber.trim()) {
      alert('กรุณาใส่เลขพัสดุ');
      return;
    }

    const finalCourierCompany = newCustomCourierCompany || newCourierCompany;
    if (!finalCourierCompany) {
      alert('กรุณาเลือกหรือกรอกบริษัทขนส่ง');
      return;
    }

    try {
      await updateOrderStatus(orderId, 'shipping', {
        trackingNumber: newTrackingNumber.trim(),
        courierCompany: newCourierCompany || null,
        customCourierCompany: newCustomCourierCompany || null,
        shippingDate: new Date()
      });
      alert('อัพเดทเลขพัสดุสำเร็จ!');
      fetchOrder();
    } catch (error) {
      alert('เกิดข้อผิดพลาด: ' + error.message);
    }
  };

  const handleMarkAsDelivered = async () => {
    try {
      await updateOrderStatus(orderId, 'delivered', {
        deliveredDate: new Date()
      });
      alert(t('orders.delivered') + '!');
      fetchOrder();
    } catch (error) {
      alert('เกิดข้อผิดพลาด: ' + error.message);
    }
  };

  const handleConfirmReceived = async () => {
    if (!window.confirm(`${t('orders.confirmReceiveMessage')}\n\n${t('orders.canReviewNow')}`)) {
      return;
    }

    try {
      await updateOrderStatus(orderId, 'received', {
        receivedDate: new Date(),
        receivedBy: currentUser.uid
      });
      alert(`${t('orders.confirmReceiveSuccess')}\n\n${t('orders.canReviewNow')}`);
      fetchOrder();
    } catch (error) {
      alert('เกิดข้อผิดพลาด: ' + error.message);
    }
  };

  const loadReviewsForOrder = async (orderData) => {
    if (!currentUser || !orderData || !orderId) return;

    try {
      // Load product reviews
      const productReviewsList = [];
      for (const item of orderData.items || []) {
        if (item.productId) {
          try {
            const reviews = await loadProductReviews(item.productId);
            const myReview = reviews.find(r => 
              r.orderId === orderId && r.reviewerId === currentUser.uid
            );
            if (myReview) {
              productReviewsList.push({ productId: item.productId, productName: item.productName, review: myReview });
            }
          } catch (error) {
            console.error('Error loading product reviews:', error);
          }
        }
      }
      setProductReviews(productReviewsList);
      setHasReviewedProducts(productReviewsList.length === (orderData.items?.length || 0));

      // Load store review
      if (orderData.storeId) {
        try {
          const reviews = await loadStoreReviews(orderData.storeId);
          const myReview = reviews.find(r => 
            r.orderId === orderId && r.reviewerId === currentUser.uid
          );
          if (myReview) {
            setStoreReviews([myReview]);
            setHasReviewedStore(true);
          } else {
            setStoreReviews([]);
            setHasReviewedStore(false);
          }
        } catch (error) {
          console.error('Error loading store reviews:', error);
        }
      }
    } catch (error) {
      console.error('Error loading reviews:', error);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">กำลังโหลด...</p>
        </div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-600">ไม่พบออเดอร์</p>
          <button
            onClick={() => navigate('/marketplace/orders')}
            className="mt-4 bg-green-500 hover:bg-green-600 text-white font-semibold py-2 px-4 rounded-lg transition-colors"
          >
            กลับไปหน้ารายการออเดอร์
          </button>
        </div>
      </div>
    );
  }

  const isBuyer = currentUser?.uid === order.buyerId;
  const isSeller = currentUser?.uid === order.sellerId;

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="container mx-auto px-4 max-w-4xl">
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <div className="flex justify-between items-start mb-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-800 mb-2">ออเดอร์ #{order.orderNumber}</h1>
              <p className="text-gray-600">วันที่: {new Date(order.createdAt?.toDate()).toLocaleDateString('th-TH')}</p>
            </div>
            <div className={`px-4 py-2 rounded-full text-sm font-semibold ${
              order.status === 'pending_payment' ? 'bg-yellow-100 text-yellow-800' :
              order.status === 'paid' ? 'bg-blue-100 text-blue-800' :
              order.status === 'shipping' ? 'bg-purple-100 text-purple-800' :
              order.status === 'delivered' ? 'bg-green-100 text-green-800' :
              'bg-gray-100 text-gray-800'
            }`}>
              {order.status === 'pending_payment' ? 'รอชำระเงิน' :
               order.status === 'paid' ? 'ชำระเงินแล้ว' :
               order.status === 'shipping' ? 'กำลังจัดส่ง' :
               order.status === 'delivered' ? t('orders.delivered') :
               order.status}
            </div>
          </div>

          {/* Order Info */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <div>
              <h3 className="font-semibold text-gray-700 mb-2">ผู้ซื้อ</h3>
              <p className="text-gray-600">{order.buyerName}</p>
            </div>
            <div>
              <h3 className="font-semibold text-gray-700 mb-2">ผู้ขาย</h3>
              <p className="text-gray-600">{order.sellerName}</p>
            </div>
            <div>
              <h3 className="font-semibold text-gray-700 mb-2">{t('orders.store')}</h3>
              <p className="text-gray-600">{order.storeName}</p>
            </div>
            <div>
              <h3 className="font-semibold text-gray-700 mb-2">{t('orders.shippingAddress')}</h3>
              <p className="text-gray-600">{order.shippingAddress}</p>
            </div>
          </div>

          {/* Items */}
          <div className="mb-6">
            <h3 className="font-semibold text-gray-700 mb-2">{t('orders.productList')}</h3>
            <div className="space-y-2">
              {order.items?.map((item, index) => (
                <div key={index} className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                  <div>
                    <p className="font-semibold text-gray-800">{item.productName}</p>
                    <p className="text-sm text-gray-600">{t('orders.quantity')}: {item.quantity} {item.unit}</p>
                  </div>
                  <p className="text-gray-800">฿{item.subtotal?.toLocaleString()}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Summary */}
          <div className="border-t pt-4 mb-6">
            <div className="flex justify-between text-gray-600 mb-2">
              <span>{t('orders.totalItems')}:</span>
              <span>฿{order.subtotal?.toLocaleString()}</span>
            </div>
            <div className="flex justify-between text-gray-600 mb-2">
              <span>ค่าธรรมเนียม:</span>
              <span>{order.fee} เหรียญ</span>
            </div>
            <div className="flex justify-between text-lg font-bold text-gray-800 border-t pt-2">
              <span>ยอดรวม:</span>
              <span>฿{order.total?.toLocaleString()}</span>
            </div>
          </div>

          {/* Payment Proof (Buyer) */}
          {isBuyer && order.status === 'pending_payment' && !order.paymentProof && (
            <div className="mb-6 p-4 bg-yellow-50 rounded-lg border-2 border-yellow-300">
              <h3 className="font-semibold text-gray-700 mb-2">📎 แนบหลักฐานการโอน</h3>
              <p className="text-sm text-gray-600 mb-3">
                {t('orders.uploadPaymentProof')}
              </p>
              <input
                type="file"
                accept="image/*"
                onChange={handleUploadPaymentProof}
                disabled={uploading}
                className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-green-500 file:text-white hover:file:bg-green-600 disabled:opacity-50"
              />
              {uploading && <p className="text-sm text-gray-600 mt-2">⏳ {t('orders.uploading')}</p>}
            </div>
          )}

          {/* Payment Proof Display - Buyer uploaded, waiting for seller confirmation */}
          {isBuyer && order.status === 'pending_payment' && order.paymentProof && (
            <div className="mb-6 p-4 bg-blue-50 rounded-lg border-2 border-blue-300">
              <h3 className="font-semibold text-gray-700 mb-2">✅ อัพโหลดหลักฐานการโอนแล้ว</h3>
              <p className="text-sm text-blue-700 mb-3">
                📧 {t('orders.notifySeller')} หลังจากผู้ขายยืนยันแล้ว สถานะจะเปลี่ยนเป็น "ชำระเงินแล้ว"
              </p>
              <div className="mb-3">
                <PaymentProofViewer 
                  paymentProofUrl={order.paymentProof} 
                  alt="Payment Proof"
                  showLabel={true}
                />
              </div>
              <div className="flex gap-2 mt-3">
                <button
                  onClick={async () => {
                    if (window.confirm('ยืนยันว่าคุณได้ชำระเงินแล้วหรือไม่?\n\nระบบจะแจ้งเตือนผู้ขายให้ตรวจสอบและยืนยันการรับเงิน')) {
                      try {
                        // อัพเดท order เพื่อบอกว่ารอผู้ขายยืนยัน
                        await updateOrderStatus(orderId, 'pending_payment', {
                          paymentConfirmedByBuyer: true,
                          paymentConfirmedByBuyerAt: new Date()
                        });

                        // ส่งการแจ้งเตือนอีกครั้งให้ผู้ขาย (เพื่อเน้นย้ำ)
                        try {
                          await createNotification(
                            order.sellerId,
                            notificationModel.types.PAYMENT_PROOF_UPLOADED,
                            'ผู้ซื้อยืนยันว่าชำระเงินแล้ว',
                            `ผู้ซื้อได้ยืนยันว่าชำระเงินแล้วสำหรับออเดอร์ #${order.orderNumber} กรุณาตรวจสอบหลักฐานและยืนยันการรับเงิน`,
                            { orderId: orderId, orderNumber: order.orderNumber }
                          );
                        } catch (notifError) {
                          console.error('Error creating payment confirmation notification:', notifError);
                        }

                        alert(`${t('orders.confirmPaymentSuccess')}\n\n📧 ${t('orders.notifySeller')}`);
                        fetchOrder();
                      } catch (error) {
                        alert('เกิดข้อผิดพลาด: ' + error.message);
                      }
                    }
                  }}
                  className="flex-1 bg-green-500 hover:bg-green-600 text-white font-semibold py-2 px-4 rounded-lg transition-colors"
                >
                  ✅ {t('orders.confirmPaid')}
                </button>
                <button
                  onClick={async () => {
                    if (window.confirm('คุณต้องการอัพโหลดหลักฐานการโอนใหม่หรือไม่?')) {
                      // Reset payment proof
                      await updateOrderStatus(orderId, 'pending_payment', {
                        paymentProof: null,
                        paymentConfirmedByBuyer: false
                      });
                      alert('ลบหลักฐานการโอนแล้ว กรุณาอัพโหลดใหม่');
                      fetchOrder();
                    }
                  }}
                  className="px-4 py-2 border border-red-300 text-red-600 hover:bg-red-50 font-semibold rounded-lg transition-colors"
                >
                  {t('orders.deleteAndReupload')}
                </button>
              </div>
            </div>
          )}

          {/* Payment Proof Display - For Seller to confirm */}
          {isSeller && order.status === 'pending_payment' && order.paymentProof && (
            <div className="mb-6 p-4 bg-yellow-50 rounded-lg border-2 border-yellow-300">
              <h3 className="font-semibold text-gray-700 mb-2">📎 หลักฐานการโอนจากผู้ซื้อ</h3>
              {order.paymentConfirmedByBuyer ? (
                <div className="mb-3 p-3 bg-green-100 rounded-lg border border-green-300">
                  <p className="text-sm text-green-800 font-semibold mb-1">
                    ✅ ผู้ซื้อยืนยันว่าชำระเงินแล้ว
                  </p>
                  <p className="text-sm text-green-700">
                    กรุณาตรวจสอบหลักฐานการโอนและยืนยันการรับเงิน
                  </p>
                </div>
              ) : (
                <p className="text-sm text-yellow-700 mb-3">
                  {t('orders.buyerUploadedProof')}
                </p>
              )}
              <div className="mb-3">
                <PaymentProofViewer 
                  paymentProofUrl={order.paymentProof} 
                  alt="Payment Proof"
                  showLabel={true}
                />
              </div>
            </div>
          )}

          {/* Payment Proof Display - Already confirmed */}
          {order.status !== 'pending_payment' && order.paymentProof && (
            <div className="mb-6">
              <h3 className="font-semibold text-gray-700 mb-2">📎 หลักฐานการโอน</h3>
              <PaymentProofViewer 
                paymentProofUrl={order.paymentProof} 
                alt="Payment Proof"
                showLabel={true}
              />
            </div>
          )}

          {/* Tracking Number (Seller) */}
          {isSeller && order.status === 'paid' && (
            <div className="mb-6 p-4 bg-blue-50 rounded-lg border-2 border-blue-300">
              <h3 className="font-semibold text-gray-700 mb-4">📦 แปะเลขพัสดุ</h3>
              <TrackingNumberInput
                value={trackingNumber}
                courierCompany={courierCompany}
                customCourierCompany={customCourierCompany}
                onChange={(newTrackingNumber, newCourierCompany, newCustomCourierCompany) => {
                  setTrackingNumber(newTrackingNumber);
                  setCourierCompany(newCourierCompany);
                  setCustomCourierCompany(newCustomCourierCompany);
                }}
                onSave={handleUpdateTrackingNumber}
              />
            </div>
          )}

          {/* Tracking Number Display */}
          {order.trackingNumber && (
            <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
              <h3 className="font-semibold text-gray-700 mb-3">📦 เลขพัสดุ</h3>
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <span className="text-gray-600 font-medium">เลขพัสดุ:</span>
                  <span className="text-gray-800 font-semibold">{order.trackingNumber}</span>
                </div>
                {(order.courierCompany || order.customCourierCompany) && (
                  <div className="flex items-center gap-2">
                    <span className="text-gray-600 font-medium">บริษัทขนส่ง:</span>
                    <span className="text-gray-800 font-semibold">{order.customCourierCompany || order.courierCompany}</span>
                  </div>
                )}
                {order.courierCompany && getTrackingUrl(order.trackingNumber, order.courierCompany) && (
                  <div className="pt-2">
                    <a
                      href={getTrackingUrl(order.trackingNumber, order.courierCompany)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 text-sm text-blue-600 hover:text-blue-700 font-medium hover:underline"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                      </svg>
                      เปิดหน้า Tracking ของ {order.courierCompany}
                    </a>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Chat Section */}
          {chatId && otherUser && (
            <div className="mb-6">
              <button
                onClick={() => setShowChat(!showChat)}
                className="w-full bg-green-500 hover:bg-green-600 text-white font-semibold py-2 px-4 rounded-lg transition-colors flex items-center justify-center gap-2"
              >
                💬 {showChat ? 'ซ่อนแชท' : 'เปิดแชทกับ ' + otherUser.name}
              </button>
              {showChat && (
                <div className="mt-4 h-96">
                  <ChatWindow
                    chatId={chatId}
                    otherUserId={otherUser.id}
                    otherUserName={otherUser.name}
                    onClose={() => setShowChat(false)}
                  />
                </div>
              )}
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-4 flex-wrap">
            {isSeller && order.status === 'pending_payment' && order.paymentProof && (
              <button
                onClick={async () => {
                  if (window.confirm('ยืนยันว่าผู้ซื้อได้ชำระเงินแล้วหรือไม่?\n\nหลังจากยืนยันแล้ว สถานะจะเปลี่ยนเป็น "ชำระเงินแล้ว" และคุณสามารถแปะเลขพัสดุได้')) {
                    try {
                      await updateOrderStatus(orderId, 'paid', { 
                        paymentDate: new Date(),
                        paymentConfirmedBy: currentUser.uid,
                        paymentConfirmedAt: new Date()
                      });
                      alert(t('orders.confirmPaymentSuccess'));
                      fetchOrder();
                    } catch (error) {
                      alert('เกิดข้อผิดพลาด: ' + error.message);
                    }
                  }
                }}
                className="bg-blue-500 hover:bg-blue-600 text-white font-semibold py-2 px-4 rounded-lg transition-colors"
              >
                ✅ {t('orders.confirmPayment')}
              </button>
            )}
            {isSeller && order.status === 'paid' && (
              <>
                <button
                  onClick={() => setShowPackingSlipModal(true)}
                  className="bg-orange-500 hover:bg-orange-600 text-white font-semibold py-2 px-4 rounded-lg transition-colors flex items-center gap-2"
                >
                  📋 {t('orders.printPackingSlip')}
                </button>
                <button
                  onClick={() => setShowShippingLabelModal(true)}
                  className="bg-purple-500 hover:bg-purple-600 text-white font-semibold py-2 px-4 rounded-lg transition-colors flex items-center gap-2"
                >
                  🖨️ {t('orders.printShippingLabel')}
                </button>
              </>
            )}
            {isSeller && order.status === 'shipping' && (
              <>
                <button
                  onClick={() => setShowPackingSlipModal(true)}
                  className="bg-orange-500 hover:bg-orange-600 text-white font-semibold py-2 px-4 rounded-lg transition-colors flex items-center gap-2"
                >
                  📋 {t('orders.printPackingSlip')}
                </button>
                <button
                  onClick={() => setShowShippingLabelModal(true)}
                  className="bg-purple-500 hover:bg-purple-600 text-white font-semibold py-2 px-4 rounded-lg transition-colors flex items-center gap-2"
                >
                  🖨️ {t('orders.printShippingLabel')}
                </button>
                <button
                  onClick={handleMarkAsDelivered}
                  className="bg-green-500 hover:bg-green-600 text-white font-semibold py-2 px-4 rounded-lg transition-colors"
                >
                  {t('orders.delivered')}
                </button>
              </>
            )}
            {isBuyer && (order.status === 'delivered' || order.status === 'shipping') && (
              <button
                onClick={handleConfirmReceived}
                className="bg-green-500 hover:bg-green-600 text-white font-semibold py-2 px-4 rounded-lg transition-colors flex items-center gap-2"
              >
                ✅ {t('orders.received')}
              </button>
            )}
            <button
              onClick={() => navigate('/marketplace/orders')}
              className="border border-gray-300 hover:bg-gray-100 text-gray-700 font-semibold py-2 px-4 rounded-lg transition-colors"
            >
              {t('common.back')}
            </button>
          </div>

          {/* Review Section - Show after received */}
          {isBuyer && order.status === 'received' && (
            <div className="mt-6 p-6 bg-gradient-to-r from-green-50 to-blue-50 rounded-lg border-2 border-green-200">
              <h3 className="text-xl font-bold text-gray-800 mb-4">{t('orders.reviewProductsAndStore')}</h3>
              
              {/* Product Reviews */}
              <div className="mb-6">
                <h4 className="font-semibold text-gray-700 mb-3">{t('marketplace.productReviews')}</h4>
                <div className="space-y-3">
                  {order.items?.map((item, index) => {
                    const existingReview = productReviews.find(pr => pr.productId === item.productId);
                    return (
                      <div key={index} className="p-4 bg-white rounded-lg border border-gray-200">
                        <div className="flex justify-between items-start mb-3">
                          <div>
                            <p className="font-semibold text-gray-800">{item.productName}</p>
                            <p className="text-sm text-gray-600">{t('orders.quantity')}: {item.quantity} {item.unit}</p>
                          </div>
                          {existingReview && (
                            <span className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-xs font-semibold">
                              ✅ รีวิวแล้ว
                            </span>
                          )}
                        </div>
                        {existingReview ? (
                          <div className="p-3 bg-gray-50 rounded-lg">
                            <div className="flex items-center gap-2 mb-2">
                              <div className="flex">
                                {[1, 2, 3, 4, 5].map(star => (
                                  <span key={star} className={star <= existingReview.review.rating ? 'text-yellow-400' : 'text-gray-300'}>
                                    ★
                                  </span>
                                ))}
                              </div>
                              <span className="text-sm font-semibold text-gray-700">{existingReview.review.title}</span>
                            </div>
                            <p className="text-sm text-gray-600">{existingReview.review.comment}</p>
                            <button
                              onClick={() => {
                                setShowProductReviewForm(item.productId);
                              }}
                              className="mt-2 text-sm text-blue-600 hover:text-blue-700 underline"
                            >
                              แก้ไขรีวิว
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={() => {
                              setShowProductReviewForm(item.productId);
                            }}
                            className="w-full py-2 px-4 bg-blue-500 hover:bg-blue-600 text-white font-semibold rounded-lg transition-colors"
                          >
                            ✍️ {t('marketplace.writeReview')}
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Store Review */}
              <div>
                <h4 className="font-semibold text-gray-700 mb-3">{t('store.reviews')}</h4>
                <div className="p-4 bg-white rounded-lg border border-gray-200">
                  <div className="flex justify-between items-start mb-3">
                    <div>
                      <p className="font-semibold text-gray-800">{order.storeName}</p>
                    </div>
                    {hasReviewedStore && storeReviews.length > 0 && (
                      <span className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-xs font-semibold">
                        ✅ รีวิวแล้ว
                      </span>
                    )}
                  </div>
                  {hasReviewedStore && storeReviews.length > 0 ? (
                    <div className="p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-2 mb-2">
                        <div className="flex">
                          {[1, 2, 3, 4, 5].map(star => (
                            <span key={star} className={star <= storeReviews[0].rating ? 'text-yellow-400' : 'text-gray-300'}>
                              ★
                            </span>
                          ))}
                        </div>
                        <span className="text-sm font-semibold text-gray-700">{storeReviews[0].title}</span>
                      </div>
                      <p className="text-sm text-gray-600">{storeReviews[0].comment}</p>
                      <button
                        onClick={() => {
                          setShowStoreReviewForm(true);
                        }}
                        className="mt-2 text-sm text-blue-600 hover:text-blue-700 underline"
                      >
                        แก้ไขรีวิว
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => {
                        setShowStoreReviewForm(true);
                      }}
                      className="w-full py-2 px-4 bg-green-500 hover:bg-green-600 text-white font-semibold rounded-lg transition-colors"
                    >
                      ✍️ {t('marketplace.writeReview')}
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Product Review Modal */}
          {showProductReviewForm && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
              <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                <div className="p-4 border-b flex justify-between items-center sticky top-0 bg-white">
                  <h3 className="text-lg font-semibold">{t('marketplace.productReviews')}</h3>
                  <button
                    onClick={() => setShowProductReviewForm(null)}
                    className="text-gray-500 hover:text-gray-700 text-xl"
                  >
                    ✕
                  </button>
                </div>
                <div className="p-4">
                  {(() => {
                    const item = order.items?.find(item => item.productId === showProductReviewForm);
                    if (!item) return null;
                    return (
                      <ProductReviewForm
                        product={{ id: item.productId, name: item.productName }}
                        orderId={orderId}
                        onClose={() => setShowProductReviewForm(null)}
                        onSuccess={async () => {
                          setShowProductReviewForm(null);
                          await loadReviewsForOrder(order);
                          fetchOrder();
                        }}
                      />
                    );
                  })()}
                </div>
              </div>
            </div>
          )}

          {/* Store Review Modal */}
          {showStoreReviewForm && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
              <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                <div className="p-4 border-b flex justify-between items-center sticky top-0 bg-white">
                  <h3 className="text-lg font-semibold">{t('store.reviews')}</h3>
                  <button
                    onClick={() => setShowStoreReviewForm(false)}
                    className="text-gray-500 hover:text-gray-700 text-xl"
                  >
                    ✕
                  </button>
                </div>
                <div className="p-4">
                  <StoreReviewForm
                    store={{ id: order.storeId, name: order.storeName }}
                    orderId={orderId}
                    onClose={() => setShowStoreReviewForm(false)}
                    onSuccess={async () => {
                      setShowStoreReviewForm(false);
                      await loadReviewsForOrder(order);
                      fetchOrder();
                    }}
                  />
                </div>
              </div>
            </div>
          )}

          {/* Shipping Label Print Modal */}
          {showShippingLabelModal && (
            <ShippingLabelPrintModal
              isOpen={showShippingLabelModal}
              onClose={() => setShowShippingLabelModal(false)}
              order={order}
              store={store}
              packageType={packageType}
            />
          )}

          {/* Packing Slip Print Modal */}
          {showPackingSlipModal && (
            <PackingSlipPrintModal
              isOpen={showPackingSlipModal}
              onClose={() => setShowPackingSlipModal(false)}
              order={order}
              store={store}
              packageType={packageType}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default OrderDetail;


